import { COLORS } from '@/constants';
import { Box, Button, Checkbox, Input, Modal, Typography } from '@servicepipe/ui-components';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  border: 1px solid ${COLORS.grayDivider};
  border-radius: 4px;
  overflow: hidden;
  box-sizing: border-box;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
  padding: 8px 12px;
  background: ${COLORS.grayLight};
  border-bottom: 1px solid ${COLORS.grayDivider};
  align-items: center;
`;

const TableBody = styled.div`
  max-height: 320px;
  overflow-y: auto;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
  padding: 8px 12px;
  align-items: center;
  border-bottom: 1px solid ${COLORS.grayDivider};
  background: ${({ $selected, $disabled }) =>
    $selected && !$disabled
      ? 'rgba(0, 123, 255, 0.08)'
      : $disabled
        ? COLORS.grayLight
        : COLORS.whiteHighEmphasis};
  color: ${({ $disabled }) => ($disabled ? COLORS.grayDark : COLORS.blackHighEmphasis)};

  &:last-child {
    border-bottom: none;
  }
`;

export const ModalAddItems = ({
  isShow,
  title,
  searchPlaceholder = 'Search',
  primaryHeader = 'Name',
  secondaryHeader = 'Details',
  items,
  alreadyAddedIds,
  loading,
  getId,
  getPrimary,
  getSecondary,
  onClose,
  onAdd,
  submitting,
}) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (!isShow) {
      setSearch('');
      setSelectedIds([]);
    }
  }, [isShow]);

  const addedSet = useMemo(() => new Set(alreadyAddedIds || []), [alreadyAddedIds]);

  const filteredItems = useMemo(() => {
    const normalized = search.trim().toLowerCase();

    if (!normalized) {
      return items;
    }

    return items.filter((item) =>
      `${getPrimary(item)} ${getSecondary?.(item) || ''}`.toLowerCase().includes(normalized)
    );
  }, [items, search, getPrimary, getSecondary]);

  const selectableIds = useMemo(
    () =>
      filteredItems
        .map((item) => getId(item))
        .filter((itemId) => itemId != null && !addedSet.has(itemId)),
    [filteredItems, addedSet, getId]
  );

  const allSelected =
    selectableIds.length > 0 && selectableIds.every((itemId) => selectedIds.includes(itemId));

  const handleClose = () => {
    setSearch('');
    setSelectedIds([]);
    onClose();
  };

  if (!isShow) {
    return null;
  }

  return (
    <Modal
      enableMaximize={false}
      size="m"
      title={title}
      toggle={handleClose}
      footer={
        <Box display="flex" justify="flex-end" gap="8px" width="100%" padding="0 8px">
          <Button variant="outline" color="neutral" type="button" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            color="accent"
            type="button"
            isDisabled={!selectedIds.length || submitting}
            isLoading={submitting}
            onClick={() => onAdd(selectedIds)}
          >
            Add
          </Button>
        </Box>
      }
    >
      <Box direction="column" gap="16px" width="100%">
        <Input
          width="100%"
          name="search"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        <TableWrapper>
          <TableHeader>
            <Box align="center" justify="center">
              <Checkbox
                checked={allSelected}
                disabled={selectableIds.length === 0 || loading}
                onChange={() => {
                  const shouldSelect = !allSelected;
                  setSelectedIds(shouldSelect ? selectableIds : []);
                }}
              />
            </Box>
            <Typography variant="bodySB">{primaryHeader}</Typography>
            <Typography variant="bodySB">{secondaryHeader}</Typography>
          </TableHeader>

          <TableBody>
            {loading && (
              <Box padding="16px">
                <Typography variant="bodySR">Loading...</Typography>
              </Box>
            )}

            {!loading && !filteredItems.length && (
              <Box padding="16px">
                <Typography variant="bodySR">No items found.</Typography>
              </Box>
            )}

            {!loading &&
              filteredItems.map((item) => {
                const itemId = getId(item);
                const isAdded = addedSet.has(itemId);
                const isSelected = selectedIds.includes(itemId);

                return (
                  <TableRow key={itemId} $disabled={isAdded} $selected={isSelected}>
                    <Box align="center" justify="center">
                      <Checkbox
                        checked={isAdded || isSelected}
                        disabled={isAdded}
                        onChange={() =>
                          setSelectedIds((prev) =>
                            prev.includes(itemId)
                              ? prev.filter((value) => value !== itemId)
                              : [...prev, itemId]
                          )
                        }
                      />
                    </Box>
                    <Typography variant="bodySR">{getPrimary(item) || '-'}</Typography>
                    <Typography variant="bodySR">{getSecondary?.(item) || '-'}</Typography>
                  </TableRow>
                );
              })}
          </TableBody>
        </TableWrapper>
      </Box>
    </Modal>
  );
};
